// Landing screen for an English diagnostics activity, and the point a student comes back to.
// Toggles into <EngDiagAttemptPlayer> on start.
//
// Same shape and chrome as bmd-welcome: one card saying what the activity is, how far through
// it they are, and one button that carries on from exactly where they stopped.
//
// THE YEAR LEVEL IS NOT SHOWN, for the same reason it is left off the Math screen: the year a
// student is assessed at is tutor context. The session returns it because the report needs it.

import React from "react"
import { Container } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import { EngDiagAttemptPlayer } from "../../modules/eng-diag/components/EngDiagAttemptPlayer"
import { getEngDiagSessionState } from "../../modules/eng-diag/services/eng-diag.service"
import type { EngDiagSessionState, EngDiagTopic } from "../../modules/eng-diag/types/eng-diag.types"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import { ENG_DIAG_COPY as COPY } from "../../constants/diagnostics/eng-diag.constants"

/** Roughly how long the test takes, for the "about N minutes" line. */
const SECONDS_PER_QUESTION_ESTIMATE = 45

/**
 * Which pill a row shows. Neither number answers it alone: what is still owed says whether
 * anything is LEFT, what has been answered says whether anything has STARTED. A block nobody
 * has opened owes all its questions, so reading only the first would badge it "In progress".
 *
 * Takes the counts rather than a block, because the rows are strands now and a strand's counts
 * are the sum of its blocks'. The rule survives the summing: a strand owes nothing only when
 * every block in it owes nothing, and has started as soon as any one of them has.
 */
function blockState(counts: {
  answered_count: number
  unserved_question_count: number
}): "done" | "active" | "upcoming" {
  if (counts.unserved_question_count === 0) return "done"
  return counts.answered_count > 0 ? "active" : "upcoming"
}

/** One row of the overview: a strand, and the blocks under it rolled into single counts. */
type StrandRow = {
  name: string
  question_count: number
  answered_count: number
  unserved_question_count: number
}

/**
 * The blocks of the session, rolled up to one row per strand.
 *
 * Rows follow the order the server queued the blocks in, so they are the order the student will
 * actually meet them in - first appearance wins, and nothing is sorted here. A block whose
 * strand is missing stands as its own row under its own name, rather than every such block
 * collapsing into one unlabelled row.
 */
function toStrandRows(topics: EngDiagTopic[]): StrandRow[] {
  const rows: StrandRow[] = []
  const byName = new Map<string, StrandRow>()

  for (const topic of topics) {
    const name = topic.cluster_name || topic.topic_name
    let row = byName.get(name)
    if (!row) {
      row = { name, question_count: 0, answered_count: 0, unserved_question_count: 0 }
      byName.set(name, row)
      rows.push(row)
    }
    row.question_count += topic.question_count
    row.answered_count += topic.answered_count
    row.unserved_question_count += topic.unserved_question_count
  }

  return rows
}

const PILL: Record<string, { label: string; className: string }> = {
  done: { label: COPY.SECTION_DONE, className: "bg-success text-white" },
  active: { label: COPY.SECTION_IN_PROGRESS, className: "bg-warning text-dark" },
  upcoming: { label: COPY.SECTION_NEXT, className: "bg-light text-muted" },
}

export const EngDiagWelcomePage: React.FC = () => {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const activityKey = key || ""

  const [session, setSession] = React.useState<EngDiagSessionState | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [started, setStarted] = React.useState(false)

  const load = React.useCallback(async (signal?: AbortSignal) => {
    const state = await getEngDiagSessionState({ activity_key: activityKey }, signal)
    if (state.error) throw new Error(state.error)
    setSession(state)
  }, [activityKey])

  React.useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        await load(controller.signal)
      } catch (e: any) {
        if (!controller.signal.aborted) setError(e?.message || COPY.ERROR_LOAD)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [load])

  const renderHeader = () => (
    <div className="cs-pg-header bg-dark-blue py-4">
      <Container className="custom-wt">
        <div className="d-flex align-items-center">
          <div className="mt-0 border-right me-4" style={{ width: "20%" }}>
            <div
              className="text-white font-size-14 text-uppercase cursor-pointer"
              onClick={() => (started ? setStarted(false) : navigate(-1))}
              role="button"
            >
              <span>
                <i className="fas fa-chevron-left me-1"></i>
              </span>{" "}
              Back
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-grow-1">
            <h6 className="mb-0 font-size-20 text-white w-60 fw-regular">
              {session?.is_prereq_test ? COPY.PREREQ_PLAYER_TITLE : COPY.PLAYER_TITLE}
            </h6>
            <p className="mb-0 text-uppercase luminous-wht">
              <span className="text-yellow me-1">{COPY.SUBJECT_LABEL}</span>{" "}
              {session?.is_prereq_test ? COPY.PREREQ_SHORT_LABEL : COPY.SHORT_LABEL}
            </p>
          </div>
        </div>
      </Container>
    </div>
  )

  /** One shell for every state, so loading, error and loaded sit in the same place. */
  const shell = (children: React.ReactNode) => (
    <div className="page-wrapper" style={{ backgroundColor: BMD_UI.PAGE_BG }}>
      {renderHeader()}
      <Container className="custom-wt">
        <div
          className="mt-4 mb-5 p-4 p-md-5"
          style={{
            backgroundColor: BMD_UI.CARD_BG,
            borderRadius: BMD_UI.CARD_RADIUS,
            border: BMD_UI.CARD_BORDER,
          }}
        >
          {children}
        </div>
      </Container>
    </div>
  )

  if (loading) return shell(<p className="text-muted mb-0">{COPY.LOADING}</p>)
  if (error || !session) {
    return shell(<p className="text-danger mb-0">{error || COPY.ERROR_LOAD}</p>)
  }

  if (started) {
    return (
      <div className="page-wrapper" style={{ backgroundColor: BMD_UI.PAGE_BG }}>
        {renderHeader()}
        <Container className="custom-wt py-4">
          <EngDiagAttemptPlayer
            activityKey={activityKey}
            onFinished={setSession}
          />
        </Container>
      </div>
    )
  }

  const { total_questions: total, answered_questions: answered } = session
  const finished = session.flow_status === "COMPLETED" || session.current_question_key === null
  const remaining = Math.max(0, total - answered)
  const minutes = Math.max(1, Math.round((remaining * SECONDS_PER_QUESTION_ESTIMATE) / 60))
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0
  const strands = toStrandRows(session.topics)

  if (finished) {
    return shell(
      <>
        <h4 className="fw-700 text-dark mb-2">{COPY.FINISH_TITLE}</h4>
        <p className="text-muted mb-0">
          {answered} {COPY.FINISH_STAT_QUESTIONS}. {COPY.FINISH_NOTE}
        </p>
      </>,
    )
  }

  return shell(
    <>
      <h4 className="fw-700 text-dark mb-2">
        {answered > 0 ? COPY.RESUME_TITLE : COPY.WELCOME_TITLE}
      </h4>
      <p className="text-muted mb-1">{session?.is_prereq_test ? COPY.PREREQ_WELCOME_NOTE : COPY.WELCOME_NOTE}</p>
      <p className="text-muted">{COPY.SAVE_NOTE}</p>

      <div className="my-4">
        <div className="d-flex justify-content-between font-size-13 text-muted mb-1">
          <span>{answered} of {total} {COPY.ANSWERED_SUFFIX}</span>
          <span>{COPY.TIME_LEFT_PREFIX} {minutes} {COPY.TIME_LEFT_SUFFIX}</span>
        </div>
        <div className="progress" style={{ height: "8px" }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${percent}%` }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Strands, one row each - what the PM asked for, and the same shape the Math screen has.
          English has six strands but dozens of topics, so a row per topic ran to 20-odd lines
          and read as a wall; Math gets away with a row per block because its blocks are main
          topics that mostly carry their strand's own name. The counts come with the row because
          one row now stands for several blocks, which is also what Math shows per section. */}
      <ul className="list-unstyled mb-4">
        {strands.map((strand) => {
          const pill = PILL[blockState(strand)]
          return (
            <li
              key={strand.name}
              className="d-flex justify-content-between align-items-center py-2 border-bottom"
            >
              <span className="text-dark">
                {strand.name}
                <span className="text-muted font-size-12 ms-2">
                  {strand.question_count} question{strand.question_count === 1 ? "" : "s"}
                </span>
              </span>
              <span className={`badge ${pill.className}`}>{pill.label}</span>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        className="btn btn-primary fw-700 px-4"
        onClick={() => setStarted(true)}
      >
        {answered > 0 ? COPY.RESUME : COPY.START}
      </button>
    </>,
  )
}

export default EngDiagWelcomePage
